import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiImage, FiUpload, FiTrash2, FiSave } from 'react-icons/fi';
import { api } from '../../utils/api';
import { triggerRefresh, REFRESH_EVENTS } from '../../utils/refreshEvents';
import './AdminHomeImages.css';

const AdminHomeImages = () => {
  const [homeImages, setHomeImages] = useState({
    heroImage: '',
    benefitsImage: ''
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ hero: false, benefits: false });

  useEffect(() => {
    fetchHomeImages();
  }, []);

  const fetchHomeImages = async () => {
    try {
      const res = await api.getHomeImages();
      setHomeImages(res.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading({ ...uploading, [type]: true });
      try {
        const formData = new FormData();
        formData.append('image', file);

        let res;
        if (type === 'hero') {
          res = await api.uploadHeroImage(formData);
        } else {
          res = await api.uploadBenefitsImage(formData);
        }

        setHomeImages(res.data.homeImages);
        alert('Image uploadée avec succès !');
        // Déclencher le rafraîchissement sur la page d'accueil
        triggerRefresh(REFRESH_EVENTS.HOME);
      } catch (error) {
        alert('Erreur lors de l\'upload: ' + (error.response?.data?.message || error.message));
      } finally {
        setUploading({ ...uploading, [type]: false });
      }
    };
    input.click();
  };

  const handleImageDelete = async (type) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'image ${type === 'hero' ? 'hero' : 'benefits'} ?`)) {
      return;
    }

    try {
      if (type === 'hero') {
        await api.deleteHeroImage();
      } else {
        await api.deleteBenefitsImage();
      }
      await fetchHomeImages();
      alert('Image supprimée avec succès !');
      // Déclencher le rafraîchissement sur la page d'accueil
      triggerRefresh(REFRESH_EVENTS.HOME);
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUrlUpdate = async (type, url) => {
    try {
      const updateData = type === 'hero' 
        ? { heroImage: url }
        : { benefitsImage: url };
      
      await api.updateHomeImages(updateData);
      setHomeImages({ ...homeImages, ...updateData });
      alert('URL mise à jour avec succès !');
      // Déclencher le rafraîchissement sur la page d'accueil
      triggerRefresh(REFRESH_EVENTS.HOME);
    } catch (error) {
      alert('Erreur lors de la mise à jour: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-home-images">
      <motion.div
        className="admin-header-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Gestion des Images - Page d'Accueil</h2>
        <p className="admin-subtitle">Modifiez les images affichées sur la page d'accueil</p>
      </motion.div>

      <div className="images-grid">
        {/* Hero Image */}
        <motion.div
          className="image-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="image-card-header">
            <FiImage className="card-icon" />
            <h3>Image Hero</h3>
            <p className="card-description">Image principale de la section hero</p>
          </div>
          
          <div className="image-preview">
            {homeImages.heroImage ? (
              <img src={homeImages.heroImage} alt="Hero" />
            ) : (
              <div className="image-placeholder">
                <FiImage />
                <span>Aucune image</span>
              </div>
            )}
          </div>

          <div className="image-actions">
            <motion.button
              onClick={() => handleImageUpload('hero')}
              className="btn btn-primary"
              disabled={uploading.hero}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiUpload />
              <span>{uploading.hero ? 'Upload...' : 'Uploader une image'}</span>
            </motion.button>
            {homeImages.heroImage && (
              <motion.button
                onClick={() => handleImageDelete('hero')}
                className="btn btn-danger"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiTrash2 />
                <span>Supprimer</span>
              </motion.button>
            )}
          </div>

          <div className="image-url-input">
            <label>Ou entrer une URL :</label>
            <div className="url-input-group">
              <input
                type="text"
                value={homeImages.heroImage || ''}
                onChange={(e) => setHomeImages({ ...homeImages, heroImage: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              <motion.button
                onClick={() => handleUrlUpdate('hero', homeImages.heroImage)}
                className="btn btn-secondary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSave />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Benefits Image */}
        <motion.div
          className="image-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="image-card-header">
            <FiImage className="card-icon" />
            <h3>Image Benefits</h3>
            <p className="card-description">Image de la section "Pourquoi VirtualHand"</p>
          </div>
          
          <div className="image-preview">
            {homeImages.benefitsImage ? (
              <img src={homeImages.benefitsImage} alt="Benefits" />
            ) : (
              <div className="image-placeholder">
                <FiImage />
                <span>Aucune image</span>
              </div>
            )}
          </div>

          <div className="image-actions">
            <motion.button
              onClick={() => handleImageUpload('benefits')}
              className="btn btn-primary"
              disabled={uploading.benefits}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiUpload />
              <span>{uploading.benefits ? 'Upload...' : 'Uploader une image'}</span>
            </motion.button>
            {homeImages.benefitsImage && (
              <motion.button
                onClick={() => handleImageDelete('benefits')}
                className="btn btn-danger"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiTrash2 />
                <span>Supprimer</span>
              </motion.button>
            )}
          </div>

          <div className="image-url-input">
            <label>Ou entrer une URL :</label>
            <div className="url-input-group">
              <input
                type="text"
                value={homeImages.benefitsImage || ''}
                onChange={(e) => setHomeImages({ ...homeImages, benefitsImage: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              <motion.button
                onClick={() => handleUrlUpdate('benefits', homeImages.benefitsImage)}
                className="btn btn-secondary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSave />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminHomeImages;

